import { atom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { useCallback, useMemo } from 'react';
import { LINK_LABELS } from '@/constants';
import type { MissingFieldsData } from '@/state/navigation';
import { getSideBarLinkId } from '@/utils/navigation';

export const missingFieldsByInteractionAtom = atomFamily(
  (_interactionId: string) => atom<Record<string, MissingFieldsData>>({}),
);

export function useAddMissingField(interactionId: string) {
  return useAtomCallback(
    useCallback(
      (get, set, fieldName: string, data: MissingFieldsData) => {
        const missingFields = get(
          missingFieldsByInteractionAtom(interactionId),
        );
        set(missingFieldsByInteractionAtom(interactionId), {
          ...missingFields,
          [fieldName]: data,
        });
      },
      [interactionId],
    ),
  );
}

export function useRemoveMissingField(interactionId: string) {
  return useAtomCallback(
    useCallback(
      (get, set, fieldName: string) => {
        const missingFields = get(
          missingFieldsByInteractionAtom(interactionId),
        );
        if (!missingFields[fieldName]) return;
        const { [fieldName]: _, ...rest } = missingFields;
        set(missingFieldsByInteractionAtom(interactionId), rest);
      },
      [interactionId],
    ),
  );
}

export function useSetMissingFields(interactionId: string) {
  return useAtomCallback(
    useCallback(
      (_, set, missingFields: Record<string, MissingFieldsData>) => {
        set(missingFieldsByInteractionAtom(interactionId), missingFields);
      },
      [interactionId],
    ),
  );
}

export function useIsCoversheetComplete(interactionId: string) {
  const missingFields = useAtomValue(
    missingFieldsByInteractionAtom(interactionId),
  );
  return Object.keys(missingFields).length === 0;
}

export function useMissingFieldsBySidebarLinkIds(
  interactionId: string,
): Record<string, MissingFieldsData[]> {
  const missingFields = useAtomValue(
    missingFieldsByInteractionAtom(interactionId),
  );
  return useMemo(() => {
    const missingFieldsByLinkId: Record<string, MissingFieldsData[]> = {};
    Object.values(missingFields).forEach((field) => {
      const linkId = getSideBarLinkId(interactionId, [
        LINK_LABELS.COVER_SHEET,
        field.section,
      ]);
      if (!missingFieldsByLinkId[linkId]) {
        missingFieldsByLinkId[linkId] = [];
      }
      missingFieldsByLinkId[linkId].push(field);
    });
    return missingFieldsByLinkId;
  }, [interactionId, missingFields]);
}
